/**
 * Drafts of the quick composers (bug bash SW-1): the Encuentro message
 * composer, Compartir and the comment field. What the person typed survives
 * a reload of the tab (a flip of the UI switch, a refresh, a crash of the
 * WebView) and comes back in the same conversation or post. Nothing goes to
 * the server.
 *
 * Unlike the page-form drafts (form-draft.ts, localStorage, 30 days) these
 * live in sessionStorage: one tab, gone when it closes. Bounded so they
 * never grow: at most MAX_SESSION_DRAFTS entries (the oldest go first),
 * MAX_SESSION_DRAFT_CHARS per entry (a longer one is not kept), and
 * SESSION_DRAFT_MAX_AGE_MS old. Sending clears its draft;
 * `clearAllSessionDrafts()` runs on sign-out (lib/utils/sign-out.ts).
 *
 * Every draft belongs to an account (review of v1.164.0, P1-2): the key holds
 * the signed-in user's id, so the same tab signed in as someone else never
 * reads it. The owner comes from the browser's Supabase session
 * (components/session-draft-owner.tsx, in the root layout); until it is known
 * nothing is read or kept. A new owner, or none (the session expired, the
 * middleware signed the person out, an invitation was accepted and /login
 * opened), removes the drafts of every other account in this tab.
 *
 * Every access is guarded: storage can be missing (SSR), full or blocked,
 * and a draft is a convenience, never a reason to fail.
 */

export const SESSION_DRAFT_PREFIX = 'alumnia:session-draft:'
export const MAX_SESSION_DRAFTS = 20
export const MAX_SESSION_DRAFT_CHARS = 20_000
export const SESSION_DRAFT_MAX_AGE_MS = 24 * 60 * 60 * 1000

type Stored<T> = { v: 1; savedAt: number; value: T }

function storage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage
  } catch {
    return null
  }
}

function keyOf(owner: string, scope: string): string {
  return `${SESSION_DRAFT_PREFIX}${owner}:${scope}`
}

// ── The account the drafts of this tab belong to ─────────────────────────────

let currentOwner: string | null = null
const ownerListeners = new Set<() => void>()

/** The signed-in user whose drafts this tab reads and keeps; null while unknown or signed out. */
export function getSessionDraftOwner(): string | null {
  return currentOwner
}

export function subscribeSessionDraftOwner(listener: () => void): () => void {
  ownerListeners.add(listener)
  return () => {
    ownerListeners.delete(listener)
  }
}

/**
 * The session said who is signed in (null: nobody). Drafts of any other
 * account in this tab go, so the next person never sees them.
 */
export function setSessionDraftOwner(userId: string | null): void {
  const owner = userId || null
  const store = storage()
  if (store) {
    try {
      const mine = owner ? `${SESSION_DRAFT_PREFIX}${owner}:` : null
      for (const key of draftKeys(store)) {
        if (!mine || !key.startsWith(mine)) store.removeItem(key)
      }
    } catch {}
  }
  if (owner === currentOwner) return
  currentOwner = owner
  ownerListeners.forEach((listener) => listener())
}

function draftKeys(store: Storage): string[] {
  const keys: string[] = []
  for (let i = 0; i < store.length; i++) {
    const key = store.key(i)
    if (key?.startsWith(SESSION_DRAFT_PREFIX)) keys.push(key)
  }
  return keys
}

function savedAtOf(store: Storage, key: string): number {
  try {
    const parsed = JSON.parse(store.getItem(key) ?? '') as Partial<Stored<unknown>>
    return typeof parsed.savedAt === 'number' ? parsed.savedAt : 0
  } catch {
    return 0
  }
}

export function readSessionDraft<T>(owner: string, scope: string, now = Date.now()): T | null {
  const store = storage()
  if (!store || !owner) return null
  const key = keyOf(owner, scope)
  try {
    const raw = store.getItem(key)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<Stored<T>>
    if (
      parsed?.v !== 1 ||
      typeof parsed.savedAt !== 'number' ||
      !('value' in parsed) ||
      now - parsed.savedAt > SESSION_DRAFT_MAX_AGE_MS
    ) {
      store.removeItem(key)
      return null
    }
    return parsed.value as T
  } catch {
    return null
  }
}

export function writeSessionDraft<T>(
  owner: string,
  scope: string,
  value: T,
  now = Date.now()
): void {
  const store = storage()
  if (!store || !owner) return
  const key = keyOf(owner, scope)
  try {
    const raw = JSON.stringify({ v: 1, savedAt: now, value } satisfies Stored<T>)
    if (raw.length > MAX_SESSION_DRAFT_CHARS) {
      store.removeItem(key)
      return
    }
    store.setItem(key, raw)
    const others = draftKeys(store).filter((k) => k !== key)
    if (others.length >= MAX_SESSION_DRAFTS) {
      others
        .sort((a, b) => savedAtOf(store, a) - savedAtOf(store, b))
        .slice(0, others.length - MAX_SESSION_DRAFTS + 1)
        .forEach((k) => store.removeItem(k))
    }
  } catch {
    // Full or blocked storage: the composer still works, it just forgets.
  }
}

export function clearSessionDraft(owner: string, scope: string): void {
  if (!owner) return
  try {
    storage()?.removeItem(keyOf(owner, scope))
  } catch {}
}

/** Every composer draft of this tab: sign-out. */
export function clearAllSessionDrafts(): void {
  const store = storage()
  if (!store) return
  try {
    for (const key of draftKeys(store)) store.removeItem(key)
  } catch {}
}
